import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlayIcon, PauseIcon, SkipBackIcon, SkipForwardIcon, ChevronLeftIcon, UserIcon, ClockIcon, CalendarIcon, TagIcon, CheckCircleIcon, ListIcon, FileTextIcon, MapPinIcon, DollarSignIcon, UsersIcon, HeartIcon, AlertCircleIcon, SearchIcon } from 'lucide-react';
import { useRecording } from '../../contexts/RecordingContext';
export const RecordingDetails: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    getRecordingById
  } = useRecording();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeTab, setActiveTab] = useState('transcription');
  const recording = getRecordingById(id || '');
  if (!recording) {
    return <div className="flex flex-col items-center justify-center h-64" data-id="element-1289">
        <p className="text-lg text-gray-600 mb-4" data-id="element-1290">Recording not found</p>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md" onClick={() => navigate('/recordings')} data-id="element-1291">
          Back to Recordings
        </button>
      </div>;
  }
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };
  const handlePlaybackSpeedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPlaybackSpeed(parseFloat(e.target.value));
  };
  const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTime(parseInt(e.target.value));
  };
  return <div className="space-y-6" data-id="element-1292">
      {/* Back button */}
      <button className="flex items-center text-gray-600 hover:text-gray-800" onClick={() => navigate('/recordings')} data-id="element-1293">
        <ChevronLeftIcon className="h-5 w-5 mr-1" data-id="element-1294" />
        Back to Recordings
      </button>
      {/* Recording header */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1295">
        <div className="flex justify-between items-start" data-id="element-1296">
          <div data-id="element-1297">
            <h1 className="text-2xl font-bold text-gray-800" data-id="element-1298">
              {recording.title}
            </h1>
            <div className="flex items-center mt-2 space-x-4" data-id="element-1299">
              <div className="flex items-center text-sm text-gray-600" data-id="element-1300">
                <CalendarIcon className="h-4 w-4 mr-1" data-id="element-1301" />
                {formatDate(recording.date)}
              </div>
              <div className="flex items-center text-sm text-gray-600" data-id="element-1302">
                <ClockIcon className="h-4 w-4 mr-1" data-id="element-1303" />
                {formatDuration(recording.duration)}
              </div>
            </div>
          </div>
          {recording.customerId && <div className="bg-blue-50 rounded-lg p-3 flex items-center" data-id="element-1304">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3" data-id="element-1305">
                <UserIcon className="h-5 w-5" data-id="element-1306" />
              </div>
              <div data-id="element-1307">
                <p className="text-sm font-medium text-gray-900" data-id="element-1308">
                  {recording.customerId === 'cust-1' ? 'Jennifer Williams' : recording.customerId === 'cust-2' ? 'Robert Chen' : recording.customerId === 'cust-3' ? 'Michael Torres' : 'Emily Wilson'}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1309">Customer</p>
              </div>
            </div>}
        </div>
        {/* Audio player */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4" data-id="element-1310">
          <div className="flex items-center justify-between mb-3" data-id="element-1311">
            <div className="flex items-center space-x-3" data-id="element-1312">
              <button className="p-1 text-gray-600 hover:text-gray-800" data-id="element-1313">
                <SkipBackIcon className="h-5 w-5" data-id="element-1314" />
              </button>
              <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700" onClick={togglePlayback} data-id="element-1315">
                {isPlaying ? <PauseIcon className="h-5 w-5" data-id="element-1316" /> : <PlayIcon className="h-5 w-5" data-id="element-1317" />}
              </button>
              <button className="p-1 text-gray-600 hover:text-gray-800" data-id="element-1318">
                <SkipForwardIcon className="h-5 w-5" data-id="element-1319" />
              </button>
            </div>
            <div className="flex items-center" data-id="element-1320">
              <span className="text-sm text-gray-600 mr-2" data-id="element-1321">
                {formatDuration(currentTime)} /{' '}
                {formatDuration(recording.duration)}
              </span>
              <select value={playbackSpeed} onChange={handlePlaybackSpeedChange} className="text-sm border border-gray-300 rounded-md px-2 py-1" data-id="element-1322">
                <option value="0.5" data-id="element-1323">0.5x</option>
                <option value="1" data-id="element-1324">1x</option>
                <option value="1.5" data-id="element-1325">1.5x</option>
                <option value="2" data-id="element-1326">2x</option>
              </select>
            </div>
          </div>
          <div className="relative" data-id="element-1327">
            <input type="range" min="0" max={recording.duration} value={currentTime} onChange={handleTimelineChange} className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer" data-id="element-1328" />
            <div className="absolute top-0 left-0 h-2 bg-blue-500 rounded-full pointer-events-none" style={{
            width: `${currentTime / recording.duration * 100}%`
          }} data-id="element-1329"></div>
          </div>
          <div className="h-12 mt-3 bg-gray-200 rounded-lg" data-id="element-1330">
            {/* Audio waveform visualization would go here */}
            <div className="h-full w-full flex items-center justify-center" data-id="element-1331">
              <span className="text-xs text-gray-500" data-id="element-1332">
                Audio waveform visualization
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1333">
        <div className="border-b border-gray-200" data-id="element-1334">
          <div className="flex" data-id="element-1335">
            <button className={`px-4 py-3 text-sm font-medium ${activeTab === 'transcription' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('transcription')} data-id="element-1336">
              <FileTextIcon className="h-4 w-4 inline mr-2" data-id="element-1337" />
              Transcription
            </button>
            <button className={`px-4 py-3 text-sm font-medium ${activeTab === 'analysis' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('analysis')} data-id="element-1338">
              <SearchIcon className="h-4 w-4 inline mr-2" data-id="element-1339" />
              AI Analysis
            </button>
            <button className={`px-4 py-3 text-sm font-medium ${activeTab === 'actions' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('actions')} data-id="element-1340">
              <CheckCircleIcon className="h-4 w-4 inline mr-2" data-id="element-1341" />
              Action Items
            </button>
          </div>
        </div>
        {/* Tab content */}
        <div className="p-6" data-id="element-1342">
          {activeTab === 'transcription' && <div data-id="element-1343">
              <div className="flex justify-between items-center mb-4" data-id="element-1344">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-1345">
                  Conversation Transcript
                </h3>
                <div className="flex items-center space-x-2" data-id="element-1346">
                  <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50" data-id="element-1347">
                    Edit Transcript
                  </button>
                  <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50" data-id="element-1348">
                    Export
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto" data-id="element-1349">
                <div className="space-y-4" data-id="element-1350">
                  <div className="flex" data-id="element-1351">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3" data-id="element-1352">
                      <UserIcon className="h-4 w-4" data-id="element-1353" />
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-[80%]" data-id="element-1354">
                      <p className="text-sm text-gray-800" data-id="element-1355">
                        Hello, I'm interested in planning a family vacation to
                        Europe this summer. We're a family of 4 with two
                        children aged 10 and 12.
                      </p>
                      <span className="text-xs text-gray-500" data-id="element-1356">00:15</span>
                    </div>
                  </div>
                  <div className="flex justify-end" data-id="element-1357">
                    <div className="bg-blue-100 rounded-lg p-3 shadow-sm max-w-[80%]" data-id="element-1358">
                      <p className="text-sm text-gray-800" data-id="element-1359">
                        That sounds great! Europe is a wonderful destination for
                        families. Do you have any specific countries or cities
                        in mind?
                      </p>
                      <span className="text-xs text-gray-500" data-id="element-1360">00:30</span>
                    </div>
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 ml-3" data-id="element-1361">
                      <UserIcon className="h-4 w-4" data-id="element-1362" />
                    </div>
                  </div>
                  <div className="flex" data-id="element-1363">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3" data-id="element-1364">
                      <UserIcon className="h-4 w-4" data-id="element-1365" />
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-[80%]" data-id="element-1366">
                      <p className="text-sm text-gray-800" data-id="element-1367">
                        We're thinking about Italy, perhaps Rome and Florence,
                        and maybe Paris as well. We'd like to do some
                        sightseeing, but also have activities that would be fun
                        for the kids.
                      </p>
                      <span className="text-xs text-gray-500" data-id="element-1368">01:05</span>
                    </div>
                  </div>
                  {/* More transcript entries would go here */}
                </div>
              </div>
            </div>}
          {activeTab === 'analysis' && recording.analysis && <div className="space-y-6" data-id="element-1369">
              <div data-id="element-1370">
                <h3 className="text-lg font-medium text-gray-800 mb-3" data-id="element-1371">
                  Customer Summary
                </h3>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-id="element-1372">
                  <p className="text-gray-800" data-id="element-1373">
                    {recording.analysis.customerSummary}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6" data-id="element-1374">
                <div data-id="element-1375">
                  <h3 className="text-md font-medium text-gray-800 mb-3 flex items-center" data-id="element-1376">
                    <MapPinIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-1377" />
                    Destinations Mentioned
                  </h3>
                  <div className="bg-white rounded-lg p-4 border border-gray-200" data-id="element-1378">
                    <div className="flex flex-wrap gap-2" data-id="element-1379">
                      {recording.analysis.destinations.map((destination, index) => <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm" data-id="element-1380">
                            {destination}
                          </span>)}
                    </div>
                  </div>
                </div>
                <div data-id="element-1381">
                  <h3 className="text-md font-medium text-gray-800 mb-3 flex items-center" data-id="element-1382">
                    <DollarSignIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-1383" />
                    Budget Information
                  </h3>
                  <div className="bg-white rounded-lg p-4 border border-gray-200" data-id="element-1384">
                    <p className="text-gray-800 font-medium" data-id="element-1385">
                      {recording.analysis.budget || 'Not specified'}
                    </p>
                    {recording.analysis.budget && <p className="text-sm text-gray-600 mt-1" data-id="element-1386">
                        Mid-range budget, suitable for standard accommodations
                        and some premium experiences.
                      </p>}
                  </div>
                </div>
                <div data-id="element-1387">
                  <h3 className="text-md font-medium text-gray-800 mb-3 flex items-center" data-id="element-1388">
                    <UsersIcon className="h-4 w-4 text-purple-500 mr-2" data-id="element-1389" />
                    Group Size & Composition
                  </h3>
                  <div className="bg-white rounded-lg p-4 border border-gray-200" data-id="element-1390">
                    <p className="text-gray-800" data-id="element-1391">
                      {recording.analysis.groupSize || 'Not specified'} people
                    </p>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-1392">
                      {recording.analysis.groupSize === '2' ? 'Couple' : recording.analysis.groupSize === '4' ? 'Family with children' : 'Group'}
                    </p>
                  </div>
                </div>
                <div data-id="element-1393">
                  <h3 className="text-md font-medium text-gray-800 mb-3 flex items-center" data-id="element-1394">
                    <CalendarIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-1395" />
                    Travel Dates
                  </h3>
                  <div className="bg-white rounded-lg p-4 border border-gray-200" data-id="element-1396">
                    <p className="text-gray-800" data-id="element-1397">
                      {recording.analysis.travelDates || 'Not specified'}
                    </p>
                    {recording.analysis.travelDates && <p className="text-sm text-gray-600 mt-1" data-id="element-1398">
                        Peak season travel, advance booking recommended.
                      </p>}
                  </div>
                </div>
              </div>
              <div data-id="element-1399">
                <h3 className="text-md font-medium text-gray-800 mb-3 flex items-center" data-id="element-1400">
                  <HeartIcon className="h-4 w-4 text-red-500 mr-2" data-id="element-1401" />
                  Interests & Preferences
                </h3>
                <div className="bg-white rounded-lg p-4 border border-gray-200" data-id="element-1402">
                  <div className="flex flex-wrap gap-2" data-id="element-1403">
                    {recording.analysis.interests.map((interest, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm" data-id="element-1404">
                        {interest}
                      </span>)}
                  </div>
                </div>
              </div>
              <div data-id="element-1405">
                <h3 className="text-md font-medium text-gray-800 mb-3 flex items-center" data-id="element-1406">
                  <ListIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-1407" />
                  Key Requirements
                </h3>
                <div className="bg-white rounded-lg p-4 border border-gray-200" data-id="element-1408">
                  <ul className="space-y-2" data-id="element-1409">
                    {recording.analysis.keyRequirements.map((requirement, index) => <li key={index} className="flex items-start" data-id="element-1410">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5" data-id="element-1411" />
                          <span className="text-gray-800" data-id="element-1412">{requirement}</span>
                        </li>)}
                  </ul>
                </div>
              </div>
            </div>}
          {activeTab === 'actions' && recording.analysis && <div data-id="element-1413">
              <div className="flex justify-between items-center mb-4" data-id="element-1414">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-1415">
                  Action Items
                </h3>
                <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1416">
                  Add Action Item
                </button>
              </div>
              <div className="space-y-3" data-id="element-1417">
                {recording.analysis.actionItems.map((action, index) => <div key={index} className="flex items-start p-3 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-1418">
                    <div className="flex-shrink-0 mr-3" data-id="element-1419">
                      <AlertCircleIcon className="h-5 w-5 text-amber-500" data-id="element-1420" />
                    </div>
                    <div className="flex-1" data-id="element-1421">
                      <div className="flex items-center justify-between" data-id="element-1422">
                        <p className="text-sm font-medium text-gray-900" data-id="element-1423">
                          {action}
                        </p>
                        <div className="flex items-center" data-id="element-1424">
                          <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded" data-id="element-1425">
                            Due in 2 days
                          </span>
                          <input type="checkbox" className="ml-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-1426" />
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-gray-500" data-id="element-1427">
                        Assigned to: You
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
      {/* Recommended Products */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1428">
        <div className="flex justify-between items-center mb-4" data-id="element-1429">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-1430">
            Recommended Products
          </h3>
          <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1431">
            View All
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4" data-id="element-1432">
          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1433">
            <img src="https://images.unsplash.com/photo-1552832230-c0197dd311b5?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="European Explorer" className="h-32 w-full object-cover" data-id="element-1434" />
            <div className="p-3" data-id="element-1435">
              <h4 className="font-medium text-gray-800" data-id="element-1436">European Explorer</h4>
              <p className="text-sm text-gray-600" data-id="element-1437">10 days • Italy & France</p>
              <div className="flex justify-between items-center mt-2" data-id="element-1438">
                <p className="text-sm font-bold text-blue-600" data-id="element-1439">$2,899</p>
                <div className="flex items-center" data-id="element-1440">
                  <div className="h-1.5 w-16 bg-gray-200 rounded-full overflow-hidden" data-id="element-1441">
                    <div className="h-full bg-green-500 rounded-full" style={{
                    width: '92%'
                  }} data-id="element-1442"></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2" data-id="element-1443">92% match</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1444">
            <img src="https://images.unsplash.com/photo-1499678329028-101435549a4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Family Paris & Rome" className="h-32 w-full object-cover" data-id="element-1445" />
            <div className="p-3" data-id="element-1446">
              <h4 className="font-medium text-gray-800" data-id="element-1447">Family Paris & Rome</h4>
              <p className="text-sm text-gray-600" data-id="element-1448">8 days • Kid-friendly</p>
              <div className="flex justify-between items-center mt-2" data-id="element-1449">
                <p className="text-sm font-bold text-blue-600" data-id="element-1450">$3,199</p>
                <div className="flex items-center" data-id="element-1451">
                  <div className="h-1.5 w-16 bg-gray-200 rounded-full overflow-hidden" data-id="element-1452">
                    <div className="h-full bg-green-500 rounded-full" style={{
                    width: '87%'
                  }} data-id="element-1453"></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2" data-id="element-1454">87% match</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1455">
            <img src="https://images.unsplash.com/photo-1543429776-2782fc8e1acd?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Italian Highlights" className="h-32 w-full object-cover" data-id="element-1456" />
            <div className="p-3" data-id="element-1457">
              <h4 className="font-medium text-gray-800" data-id="element-1458">Italian Highlights</h4>
              <p className="text-sm text-gray-600" data-id="element-1459">
                7 days • Rome, Florence, Venice
              </p>
              <div className="flex justify-between items-center mt-2" data-id="element-1460">
                <p className="text-sm font-bold text-blue-600" data-id="element-1461">$2,499</p>
                <div className="flex items-center" data-id="element-1462">
                  <div className="h-1.5 w-16 bg-gray-200 rounded-full overflow-hidden" data-id="element-1463">
                    <div className="h-full bg-green-500 rounded-full" style={{
                    width: '82%'
                  }} data-id="element-1464"></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2" data-id="element-1465">82% match</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};